/**
 * Player-facing options.
 *
 * A tiny store rather than a config file: these are things the player flips at
 * runtime, they persist between runs, and the settings panel renders itself
 * from the same schema. Adding an option means adding one entry to `OPTIONS` —
 * the panel picks it up and `Game` reads it by id.
 *
 * Every read is defensive. localStorage throws in private modes and in some
 * embedded webviews, and a missing or malformed value must never stop the game
 * from booting, so a failed read just yields the default.
 */
const STORAGE_KEY = 'k-town-craft:settings';

/**
 * The schema. `type` decides which control the panel renders; `label` and
 * `hint` are shown as-is, so they are written for the player, not for us.
 */
export const OPTIONS = [
  {
    id: 'autoDrive',
    type: 'toggle',
    label: 'Auto-drive',
    hint: 'The Hilux pulls away on its own. Steer with the wheel and shift between D and R.',
    defaultValue: false,
  },
];

const DEFAULTS = OPTIONS.reduce((defaults, option) => {
  defaults[option.id] = option.defaultValue;
  return defaults;
}, {});

export class Settings {
  constructor() {
    this.values = { ...DEFAULTS };
    this.listeners = new Set();
    this.load();
  }

  get(id) {
    return this.values[id];
  }

  set(id, value) {
    if (!(id in DEFAULTS)) return false;
    if (this.values[id] === value) return false;
    this.values[id] = value;
    this.save();
    for (const listener of this.listeners) listener(id, value);
    return true;
  }

  toggle(id) {
    if (typeof this.values[id] !== 'boolean') return false;
    return this.set(id, !this.values[id]);
  }

  /** Subscribe to changes. Returns an unsubscribe function. */
  onChange(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  load() {
    let stored;
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    } catch {
      return;
    }
    if (!stored || typeof stored !== 'object') return;

    for (const option of OPTIONS) {
      const value = stored[option.id];
      // Only accept a stored value of the declared type, so an older build's
      // setting cannot poison the current one.
      if (typeof value === typeof option.defaultValue) {
        this.values[option.id] = value;
      }
    }
  }

  save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.values));
    } catch {
      /* the option still applies for this session */
    }
  }
}
